import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Observable, from, map, firstValueFrom, switchMap } from 'rxjs';
import { AuthService } from '../auth.service';
import { TaxDocument, TaxDocumentStatus, ResidenceChangeRequest, ResidenceChangeRequestInput } from '../../interfaces/tax/tax-document.interface';
import { Timestamp } from 'firebase/firestore';
import firebase from 'firebase/compat/app';

@Injectable({
  providedIn: 'root'
})
export class DocumentManagementService {
  private readonly COLLECTION_NAME = 'fiscal-documents';
  private readonly RESIDENCE_CHANGES_COLLECTION = 'residence-change-requests';
  private readonly STORAGE_PATH = 'fiscal-documents';

  constructor(
    private afs: AngularFirestore,
    private storage: AngularFireStorage,
    private authService: AuthService
  ) {}

  /**
   * Obtiene todos los documentos fiscales ordenados por fecha de subida
   */
  getAllDocuments(): Observable<TaxDocument[]> {
    return this.afs.collection<TaxDocument>(this.COLLECTION_NAME, ref => 
      ref.orderBy('uploadDate', 'desc')
    ).valueChanges({ idField: 'id' });
  }

  /**
   * Obtiene los documentos de un usuario específico
   */
  getUserDocuments(userId: string): Observable<TaxDocument[]> {
    return this.afs.collection<TaxDocument>(this.COLLECTION_NAME, ref => 
      ref.where('userId', '==', userId)
        .orderBy('uploadDate', 'desc')
    ).valueChanges({ idField: 'id' });
  }

  /**
   * Obtiene el documento más reciente de un usuario
   */
  getCurrentDocument(userId: string): Observable<TaxDocument | null> {
    return this.afs.collection<TaxDocument>(this.COLLECTION_NAME, ref =>
      ref.where('userId', '==', userId)
        .orderBy('uploadDate', 'desc')
        .limit(1)
    ).valueChanges({ idField: 'id' }).pipe(
      map(docs => docs.length > 0 ? docs[0] : null)
    );
  }

  /**
   * Verifica si un usuario tiene un documento activo
   */
  async hasActiveDocument(userId: string): Promise<TaxDocument | null> {
    const docs = await firstValueFrom(
      this.afs.collection<TaxDocument>(this.COLLECTION_NAME, ref => 
        ref.where('userId', '==', userId)
          .where('status', 'in', ['pending', 'approved', 'change_requested', 'change_approved'])
          .orderBy('uploadDate', 'desc')
          .limit(1)
      ).valueChanges({ idField: 'id' })
    );
    
    return docs && docs.length > 0 ? docs[0] : null;
  }

  /**
   * Obtiene documentos que expiran pronto
   */
  getExpiringDocuments(daysThreshold: number = 30): Observable<TaxDocument[]> {
    const thresholdDate = new Date();
    thresholdDate.setDate(thresholdDate.getDate() + daysThreshold);
    
    return this.afs.collection<TaxDocument>(this.COLLECTION_NAME, ref => 
      ref.where('status', '==', 'approved')
        .where('expiryDate', '<=', thresholdDate)
        .where('expiryDate', '>=', new Date())
        .orderBy('expiryDate', 'asc')
    ).valueChanges({ idField: 'id' });
  }

  /**
   * Aprueba un documento con porcentaje de retención y fecha de expiración
   */
  approveDocument(
    documentId: string, 
    retentionPercentage: number,
    expiryDate: Date,
    countryOfResidence: string
  ): Observable<void> {
    return from(
      this.afs.collection(this.COLLECTION_NAME).doc(documentId).update({
        status: 'approved',
        approvalDate: firebase.firestore.Timestamp.now(),
        expiryDate: firebase.firestore.Timestamp.fromDate(expiryDate),
        retentionPercentage,
        countryOfResidence
      })
    );
  }

  /**
   * Rechaza un documento con motivo
   */
  rejectDocument(documentId: string, reason: string): Observable<void> {
    return from(
      this.afs.collection(this.COLLECTION_NAME).doc(documentId).update({
        status: 'rejected',
        rejectionReason: reason,
        rejectionDate: firebase.firestore.Timestamp.now()
      })
    );
  }

  /**
   * Solicita un cambio de documento
   */
  requestDocumentChange(documentId: string): Observable<void> {
    return from(
      this.afs.collection(this.COLLECTION_NAME).doc(documentId).update({
        status: 'change_requested',
        isChangeRequest: true,
        changeRequestDate: firebase.firestore.Timestamp.now()
      })
    );
  }

  /**
   * Aprueba una solicitud de cambio
   */
  approveChangeRequest(documentId: string): Observable<void> {
    const now = new Date();
    const expiryDate = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
    
    return from(
      this.afs.collection(this.COLLECTION_NAME).doc(documentId).update({
        status: 'approved',
        approvalDate: firebase.firestore.Timestamp.now(),
        expiryDate: firebase.firestore.Timestamp.fromDate(expiryDate),
        isChangeRequest: false
      })
    );
  }

  /**
   * Rechaza una solicitud de cambio
   */
  rejectChangeRequest(documentId: string, reason: string): Observable<void> {
    return from(
      this.afs.collection(this.COLLECTION_NAME).doc(documentId).update({
        status: 'change_rejected',
        rejectionReason: reason,
        rejectionDate: firebase.firestore.Timestamp.now(),
        isChangeRequest: false
      })
    );
  }

  /**
   * Actualiza la fecha de expiración de un documento
   */
  updateExpiryDate(documentId: string, expiryDate: Date): Observable<void> {
    return from(
      this.afs.collection(this.COLLECTION_NAME).doc(documentId).update({
        expiryDate: firebase.firestore.Timestamp.fromDate(expiryDate)
      })
    );
  }

  /**
   * Actualiza el porcentaje de retención de un usuario
   */
  updateUserRetentionPercentage(userId: string, percentage: number): Observable<void> {
    const userDoc = this.afs.collection('users').doc(userId);

    return userDoc.get().pipe(
      switchMap(snapshot => {
        const userData = snapshot.data() as any | undefined;
        const updates = this.buildUserRetentionUpdatePayload(userData, percentage);
        const writePromise = snapshot.exists
          ? userDoc.update(updates)
          : userDoc.set(updates, { merge: true });
        return from(writePromise);
      }),
      map(() => undefined)
    );
  }

  /**
   * Sube un nuevo documento fiscal
   */
  async uploadDocument(file: File, country: string, isChangeRequest: boolean = false): Promise<void> {
    const user = this.authService.userAuth.getValue();
    if (!user) throw new Error('No hay usuario autenticado');

    const filePath = `${this.STORAGE_PATH}/${user.uid}/${Date.now()}_${file.name}`;
    const fileRef = this.storage.ref(filePath);
    const task = this.storage.upload(filePath, file);

    await task;
    const url = await fileRef.getDownloadURL().toPromise();

    const document: Partial<TaxDocument> = {
      userId: user.uid,
      userEmail: user.email || undefined,
      userName: user.displayName || undefined,
      fileName: file.name,
      fileUrl: url,
      countryOfResidence: country,
      uploadDate: Timestamp.fromDate(new Date()),
      status: isChangeRequest ? 'change_requested' : 'pending',
      isChangeRequest
    };

    if (isChangeRequest) {
      document.changeRequestDate = Timestamp.fromDate(new Date());
    }

    await this.afs.collection(this.COLLECTION_NAME).add(document);
  }

  /**
   * Elimina un documento y su archivo del storage
   */
  async deleteDocument(documentId: string): Promise<void> {
    const doc = await this.afs.collection(this.COLLECTION_NAME).doc(documentId).get().toPromise();
    if (doc?.exists) {
      const data = doc.data() as TaxDocument;
      
      // Eliminar archivo del storage
      if (data.fileUrl) {
        const storageRef = this.storage.refFromURL(data.fileUrl);
        await storageRef.delete().toPromise();
      }
      
      // Eliminar documento de Firestore
      await this.afs.collection(this.COLLECTION_NAME).doc(documentId).delete();
    }
  }

  /**
   * Solicita un cambio de residencia
   */
  async requestResidenceChange(request: ResidenceChangeRequestInput): Promise<void> {
    const user = this.authService.userAuth.getValue();
    if (!user) throw new Error('No hay usuario autenticado');

    // Auto-aprobar solo si el cambio es dentro del mismo país (no si cambia a España desde otro país)
    const shouldAutoApprove = request.isSameCountry === true;

    // Determinar el estado basado en si debe auto-aprobarse
    const status = shouldAutoApprove ? 'approved' : 'pending';

    const changeRequest: any = {
      ...request,
      userId: user.uid,
      userEmail: user.email,
      userName: user.displayName,
      requestDate: Timestamp.now(),
      status: status
    };

    // Solo agregar approvalDate si debe auto-aprobarse
    if (shouldAutoApprove) {
      changeRequest.approvalDate = Timestamp.now();
      
      // Calcular fecha de validez (1 año por defecto)
      const validityDate = new Date();
      validityDate.setFullYear(validityDate.getFullYear() + 1);
      changeRequest.validityDate = Timestamp.fromDate(validityDate);
      
      // Actualizar inmediatamente la dirección del usuario
      const retentionPercentage = request.requestedCountryRetentionPercentage ?? 19; // Valor por defecto si es undefined
      await this.updateUserAddress(user.uid, request.requestedAddress, retentionPercentage);
    }

    await this.afs.collection(this.RESIDENCE_CHANGES_COLLECTION).add(changeRequest);
  }

  /**
   * Actualiza la dirección del usuario en la base de datos
   */
  private async updateUserAddress(userId: string, requestedAddress: any, retentionPercentage: number): Promise<void> {
    try {
      // Verificar si el usuario existe
      const userDoc = await this.afs.collection('users').doc(userId).get().toPromise();
      
      if (userDoc && userDoc.exists) {
        const userData = userDoc.data() as any;
        // Preparar los datos de actualización
        const hasEmpresa = userData?.Empresa?.Datos_Empresa || userData?.empresa;
        const updateData: any = this.buildUserRetentionUpdatePayload(userData, retentionPercentage);

        if (hasEmpresa) {
          // Usuario con empresa: nomenclatura en cloud Pais_Fiscal, Provincia, Codigo_Postal (mayúsculas)
          if (requestedAddress?.country) {
            updateData['Empresa.Datos_Empresa.Pais_Fiscal'] = requestedAddress.country;
          }
          if (requestedAddress?.street) {
            updateData['Empresa.Datos_Empresa.Razon_Social'] = requestedAddress.street;
          }
          if (requestedAddress?.locality) {
            updateData['Empresa.Datos_Empresa.ciudad'] = requestedAddress.locality;
          }
          if (requestedAddress?.province) {
            updateData['Empresa.Datos_Empresa.Provincia'] = requestedAddress.province;
          }
          if (requestedAddress?.postalCode) {
            updateData['Empresa.Datos_Empresa.Codigo_Postal'] = requestedAddress.postalCode;
          }
        } else {
          // Usuario normal: actualizar en particular
          if (requestedAddress?.country) {
            updateData['particular.pais_de_residencia'] = requestedAddress.country;
          }
          if (requestedAddress?.street) {
            updateData['particular.domicilio'] = requestedAddress.street;
          }
          if (requestedAddress?.locality) {
            updateData['particular.ciudad'] = requestedAddress.locality;
          }
          if (requestedAddress?.province) {
            updateData['particular.provincia'] = requestedAddress.province;
          }
          if (requestedAddress?.postalCode) {
            updateData['particular.codigo_postal'] = requestedAddress.postalCode;
          }
        }

        // Actualizar el usuario
        await this.afs.collection('users').doc(userId).update(updateData);
        console.log('✅ Dirección del usuario actualizada exitosamente');
      } else {
        console.warn(`Usuario ${userId} no encontrado en la base de datos`);
      }
    } catch (error) {
      console.error('❌ Error al actualizar la dirección del usuario:', error);
      throw error;
    }
  }

  /**
   * Obtiene las solicitudes de cambio de residencia de un usuario
   */
  getResidenceChangeRequests(userId: string): Observable<ResidenceChangeRequest[]> {
    return this.afs.collection<ResidenceChangeRequest>(this.RESIDENCE_CHANGES_COLLECTION, ref =>
      ref.where('userId', '==', userId)
        .orderBy('requestDate', 'desc')
    ).valueChanges({ idField: 'id' });
  }

  /**
   * Verifica y actualiza documentos expirados
   */
  checkAndUpdateExpiredDocuments(): Observable<void> {
    const today = new Date();
    
    return this.afs.collection<TaxDocument>(this.COLLECTION_NAME, ref =>
      ref.where('status', '==', 'approved')
        .where('expiryDate', '<=', today)
    ).get().pipe(
      switchMap(snapshot => {
        const batch = this.afs.firestore.batch();
        snapshot.docs.forEach(doc => {
          const docRef = this.afs.collection(this.COLLECTION_NAME).doc(doc.id).ref;
          batch.update(docRef, {
            status: 'expired',
            expiryDate: Timestamp.fromDate(today)
          });
        });
        return from(batch.commit());
      })
    );
  }

  /**
   * Envía notificación de expiración
   */
  sendExpiryNotification(
    userEmail: string,
    userName: string,
    documentName: string,
    expiryDate: Date
  ): Promise<void> {
    // Implementar lógica de envío de notificaciones
    console.log(`Enviando notificación de expiración a ${userEmail} para el documento ${documentName}`);
    return Promise.resolve();
  }

  /**
   * Construye el objeto de actualización para la retención del usuario.
   * - Empresa: retentionPercentage en Empresa.Datos_Empresa
   * - Particular: retentionPercentage dentro de la colección particular
   */
  private buildUserRetentionUpdatePayload(userData: any | undefined, retentionPercentage: number, hasEmpresa?: boolean): Record<string, any> {
    if (hasEmpresa) {
      return { 'Empresa.Datos_Empresa.retentionPercentage': retentionPercentage };
    }
    return { 'particular.retentionPercentage': retentionPercentage };
  }
}
